import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { getProfileData } from "../services/userService";
import { UserProfile } from "../types/types";
import { useAuth } from "../supabase/AuthProvider";

interface UserProfileContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  profileResolved: boolean;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
);

export const useUserProfile = (): UserProfileContextType => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
};

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({
  children,
}) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileResolved, setProfileResolved] = useState(false);
  const { user, reportSessionValidation } = useAuth();

  // Prevent multiple simultaneous API calls
  const fetchingRef = useRef(false);

  const fetchUserProfile = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      console.log("Profile fetch already in progress, skipping...");
      return;
    }

    if (!user) {
      setUserProfile(null);
      setError(null);
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const profile = await getProfileData();
      setUserProfile(profile);
      reportSessionValidation({ ok: true });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch user profile";
      setError(errorMessage);
      // G2 terminal-auth: this profile call is the app's bootstrap session
      // validation. Surface the failure shape (restApiService ApiError
      // carries status + errorCode) so the AuthProvider can clear a
      // backend-rejected local session. Transient failures (network/5xx)
      // deliberately carry no 401/403 status and never clear the session.
      const maybeApiError = err as { status?: unknown; errorCode?: unknown };
      reportSessionValidation({
        ok: false,
        status:
          typeof maybeApiError?.status === "number"
            ? maybeApiError.status
            : undefined,
        errorCode:
          typeof maybeApiError?.errorCode === "string"
            ? maybeApiError.errorCode
            : undefined,
      });
    } finally {
      setLoading(false);
      setProfileResolved(true);
      fetchingRef.current = false;
    }
  }, [user, reportSessionValidation]);

  // Fetch profile when user changes
  useEffect(() => {
    if (user?.id) {
      fetchUserProfile();
    } else if (!user) {
      setUserProfile(null);
      setError(null);
      setProfileResolved(false);
    }
  }, [user, fetchUserProfile]);

  const value: UserProfileContextType = {
    userProfile,
    loading,
    error,
    profileResolved,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
